import { useCallback, useEffect, useState } from "react";
import { ApiError, partnerApi, type PartnerCabinet } from "../api";
import { Banner, CopyField, Sparkline, StatTile } from "../components/ui";
import { percent, relativeAge, shortDate } from "../format";

/**
 * The partner's own view.
 *
 * Ordered by what a partner actually wants when they open this: their link
 * first, ready to copy, because that is the thing they came for and the thing
 * they will lose. Then the numbers.
 *
 * The numbers answer the question that motivated this whole feature — plenty
 * of people open a link and never write in, and a cabinet that counted only
 * submissions would tell a partner their link was dead when the link was fine
 * and the pitch was not. So visitors and leads are shown side by side, with
 * the conversion between them.
 *
 * Nothing here reveals a referred operator's contact details. That is
 * enforced server-side (see PartnerLeadRow in the Worker) and not merely
 * omitted from this markup.
 */
export default function Cabinet({ apiKey, onSignOut }: { apiKey: string; onSignOut: () => void }) {
  const [data, setData] = useState<PartnerCabinet | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await partnerApi.me(apiKey));
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // The key was rotated or the account paused. Sending them back to the
        // login screen is the only honest response — there is nothing to show.
        onSignOut();
        return;
      }
      setError(err instanceof Error ? err.message : "Something went wrong.");
    }
  }, [apiKey, onSignOut]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <div className="shell">
        <Banner tone="error">{error}</Banner>
        <button onClick={() => void load()}>Try again</button>
      </div>
    );
  }

  if (!data) return <div className="shell dim">Loading…</div>;

  const { partner, links, stats, series, seriesDays, leads } = data;
  const live = stats.stages.live ?? 0;
  const inPlay = (stats.stages.talking ?? 0) + (stats.stages.demo ?? 0);

  return (
    <div className="shell">
      <div className="topbar">
        <div>
          <h1>{partner.displayName}</h1>
          <div className="sub">
            Partner since {shortDate(partner.since)} · referral code <code>{partner.slug}</code>
          </div>
        </div>
        <button className="ghost small" onClick={onSignOut}>
          Sign out
        </button>
      </div>

      <section>
        <h2>Your referral links</h2>
        <div className="card">
          <CopyField label="Partner landing page" value={links.landing} />
          <div style={{ height: 14 }} />
          <CopyField label="Straight to the contact form" value={links.contact} />
          <p className="dim tiny" style={{ marginBottom: 0, marginTop: 14 }}>
            Both are equivalent — anyone arriving through either is credited to you, and the
            credit survives them browsing around before they get in touch. You can also add{" "}
            <code>?ref={partner.slug}</code> to <em>any</em> page on solex.games if you want to
            link somewhere more specific.
          </p>
        </div>
      </section>

      <section>
        <h2>How the link is doing</h2>
        <div className="tiles">
          <StatTile
            label="Visitors"
            value={stats.visitors}
            hint={`${stats.visitorDays} visit${stats.visitorDays === 1 ? "" : "s"} in total`}
          />
          <StatTile label="Got in touch" value={stats.leads} hint={relativeAge(stats.lastLeadAt)} />
          <StatTile
            label="Conversion"
            value={percent(stats.conversion)}
            hint={stats.conversion === null ? "no visitors yet" : "of visitors who wrote in"}
          />
          <StatTile label="Live" value={live} hint={`${inPlay} in conversation`} />
        </div>

        <div className="card" style={{ marginTop: 12 }}>
          <div className="row spread" style={{ marginBottom: 10 }}>
            <span className="tile-label">Last {seriesDays} days</span>
            <span className="tiny dim">visitors · leads</span>
          </div>
          <Sparkline days={seriesDays} visitors={series.visitors} leads={series.leads} />
        </div>
      </section>

      <section>
        <h2>Introductions you&apos;ve made</h2>
        {leads.length === 0 ? (
          <div className="table-wrap">
            <p className="empty">
              Nobody has filled in the form through your link yet. Share it and this fills in.
            </p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Arrived</th>
                  <th>Company</th>
                  <th>Country</th>
                  <th>Stage</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.id}>
                    <td className="nowrap num">{shortDate(lead.created_at)}</td>
                    <td>{lead.company || <span className="dim">not given</span>}</td>
                    <td className="dim">{lead.country ?? "—"}</td>
                    <td>
                      <span className={`pill ${lead.stage === "live" ? "live" : ""}`}>
                        {lead.stage}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="dim tiny" style={{ marginTop: 10 }}>
          We keep the operators&apos; contact details private — you&apos;ll see the company and how
          far the conversation has got, and we&apos;ll come to you if we need an introduction.
        </p>
      </section>
    </div>
  );
}
